import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ENDPOINTS, endpointByKey, type Endpoint, type Field } from "./endpoints";
import { highlightJson } from "./highlight";
import { Icon } from "../ui/Icon";
import { Button } from "../ui/primitives";
import { FeedbackProvider, useToast } from "../ui/feedback";
import { copyText, useHotkey, useTheme } from "../lib/hooks";
import { fmtBytes, shortId } from "../lib/format";

const KEY_STORAGE = "dipnet_api_key";
const VIDEO_STORAGE = "dipnet_last_video_id";
const THEME_KEY = "dipnet_theme";

/** Where the response docks beside the form instead of below it.
 *  Must match the `.cdoc` grid breakpoint in console.css. */
const SIDE_BY_SIDE = "(min-width: 1340px)";

export function App() {
  return (
    <FeedbackProvider>
      <Console />
    </FeedbackProvider>
  );
}

/** The endpoint in view is in the URL hash, so a link points at one endpoint. */
function useHashEndpoint(): [string, (key: string) => void] {
  const read = () => location.hash.replace(/^#\/?/, "") || ENDPOINTS[0]!.key;
  const [key, setKey] = useState(read);

  useEffect(() => {
    const onHash = () => setKey(read());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  return [
    ENDPOINTS.some((e) => e.key === key) ? key : ENDPOINTS[0]!.key,
    (next: string) => {
      location.hash = `#/${next}`;
    },
  ];
}

type FieldValue = string | boolean;
type FormState = Record<string, Record<string, FieldValue>>;

interface ResponseState {
  status: number;
  ms: number;
  bytes: number;
  text: string;
  isJson: boolean;
}

/** Which step of the four-step flow an endpoint is, if it is one at all. */
const stepOf = (e: Endpoint): number | null => {
  const m = /^Step (\d+)/.exec(e.flow ?? "");
  return m ? Number(m[1]) : null;
};

function Console() {
  const toast = useToast();
  const [theme, toggleTheme] = useTheme(THEME_KEY);
  const [activeKey, setActiveKey] = useHashEndpoint();
  const endpoint = endpointByKey(activeKey);

  const [apiKey, setApiKey] = useState(() => localStorage.getItem(KEY_STORAGE) ?? "");
  const [keyVisible, setKeyVisible] = useState(false);
  const [lastVideoId, setLastVideoId] = useState(() => localStorage.getItem(VIDEO_STORAGE) ?? "");
  const [form, setForm] = useState<FormState>({});
  const [response, setResponse] = useState<ResponseState | null>(null);
  const [sending, setSending] = useState(false);
  const sendRef = useRef<() => void>(() => {});
  const respRef = useRef<HTMLElement>(null);

  useEffect(() => {
    localStorage.setItem(KEY_STORAGE, apiKey);
  }, [apiKey]);

  // Switching endpoints clears the response — a body from /add sitting under a
  // /report form reads as though /report returned it.
  useEffect(() => setResponse(null), [activeKey]);

  // A reply that lands below the fold reads as nothing having happened — but
  // only when the panes are stacked. Side by side the response is already in
  // view, and scrolling the page out from under the form would be the bug.
  useEffect(() => {
    if (!response || matchMedia(SIDE_BY_SIDE).matches) return;
    respRef.current?.scrollIntoView({
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "nearest",
    });
  }, [response]);

  const valueOf = useCallback(
    (ep: Endpoint, f: Field): FieldValue => {
      const stored = form[ep.key]?.[f.name];
      if (stored !== undefined) return stored;
      if (f.type === "checkbox") return f.default ?? false;
      if (f.autoFill && lastVideoId) return lastVideoId;
      return "";
    },
    [form, lastVideoId],
  );

  const setValue = (epKey: string, name: string, value: FieldValue) =>
    setForm((prev) => ({ ...prev, [epKey]: { ...prev[epKey], [name]: value } }));

  const send = useCallback(async () => {
    if (endpoint.auth && !apiKey) {
      toast.err(new Error("Enter your API key first — the field is in the left column"));
      return;
    }

    const req = assemble(endpoint, (f) => valueOf(endpoint, f), true);
    if ("error" in req) {
      toast.err(new Error(req.error));
      return;
    }

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (endpoint.auth) headers["key"] = apiKey;

    setSending(true);
    const started = performance.now();
    try {
      const res = await fetch(req.url, {
        method: endpoint.method,
        headers,
        body: endpoint.method === "POST" ? JSON.stringify(req.body) : undefined,
      });
      const ms = Math.round(performance.now() - started);
      const contentType = res.headers.get("content-type") ?? "";
      const isJson = contentType.includes("application/json");
      const raw = await res.text();
      const text = isJson ? safePretty(raw) : raw;

      // /add hands back the video_id every later step needs; capturing it here
      // is what lets the four-step flow be clicked through without copy-paste.
      if (endpoint.key === "add" && isJson) {
        try {
          const parsed = JSON.parse(raw) as { video_id?: string; data?: { id?: string } };
          const vid = parsed.video_id ?? parsed.data?.id;
          if (vid) {
            setLastVideoId(String(vid));
            localStorage.setItem(VIDEO_STORAGE, String(vid));
            toast.ok(`Video ID saved: ${shortId(String(vid))}…`);
          }
        } catch {
          /* a non-object body simply carries no id to capture */
        }
      }

      setResponse({ status: res.status, ms, bytes: new Blob([text]).size, text, isJson });
    } catch (err) {
      const ms = Math.round(performance.now() - started);
      const text = JSON.stringify(
        { error: "Network error", message: err instanceof Error ? err.message : String(err) },
        null,
        2,
      );
      setResponse({ status: 0, ms, bytes: new Blob([text]).size, text, isJson: true });
    } finally {
      setSending(false);
    }
  }, [endpoint, apiKey, valueOf, toast]);

  sendRef.current = send;
  useHotkey("Enter", () => sendRef.current(), { meta: true, whileTyping: true });

  /* The rail groups the API the way it is used, not the way it is served:
     the four-step protection flow in order, then the things you look up, then
     the rest. A GET/POST split describes the implementation. */
  const groups = useMemo(() => {
    const flow = ENDPOINTS.filter((e) => stepOf(e) !== null).sort((a, b) => stepOf(a)! - stepOf(b)!);
    const rest = ENDPOINTS.filter((e) => stepOf(e) === null);
    return {
      flow,
      lookups: rest.filter((e) => e.method === "GET"),
      utilities: rest.filter((e) => e.method !== "GET"),
    };
  }, []);

  const preview = assemble(endpoint, (f) => valueOf(endpoint, f), false);
  const curl = "error" in preview ? "" : buildCurl(endpoint, preview, apiKey, keyVisible);
  const missingKey = endpoint.auth && !apiKey;

  return (
    <div className="console">
      <aside className="crail">
        <div className="crail__brand">
          <img className="logo logo--sm" src="/favicon.svg" alt="" />
          <div>
            <div className="crail__name">DipNet Console</div>
            <div className="crail__sub">youtube-ip-agent · v{__APP_VERSION__}</div>
          </div>
        </div>

        <nav className="crail__nav">
          <NavGroup label="Protection flow" items={groups.flow} active={activeKey} onPick={setActiveKey} flow />
          <NavGroup label="Lookups" items={groups.lookups} active={activeKey} onPick={setActiveKey} />
          <NavGroup label="Utilities" items={groups.utilities} active={activeKey} onPick={setActiveKey} />

          <div className="crail__group">
            <div className="crail__label">Elsewhere</div>
            <div className="crail__items">
              <a className="eplink" href="/openapi.json" target="_blank" rel="noopener noreferrer">
                <span className="eplink__gut verb verb--doc">RAW</span>
                <span className="eplink__path">openapi.json</span>
                <Icon name="external" size={11} style={{ marginLeft: "auto", opacity: 0.5 }} />
              </a>
            </div>
          </div>
        </nav>

        <div className="crail__foot">
          <div className="keyhd">
            <span className="keyhd__label">API key</span>
            <span className={`keyhd__state ${apiKey ? "is-set" : ""}`}>
              {apiKey ? "set" : "not set"}
            </span>
          </div>
          <div className="keyfield">
            <input
              type={keyVisible ? "text" : "password"}
              placeholder="paste key…"
              spellCheck={false}
              autoComplete="off"
              aria-label="API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={keyVisible ? "eyeOff" : "eye"}
              title={keyVisible ? "Hide key" : "Show key"}
              onClick={() => setKeyVisible((v) => !v)}
            />
          </div>

          {lastVideoId && (
            <button
              type="button"
              className="session"
              title={`${lastVideoId} — click to copy`}
              onClick={() => void copyText(lastVideoId)}
            >
              <span className="session__k">video_id</span>
              <span className="session__v truncate">{shortId(lastVideoId)}…</span>
              <Icon name="copy" size={12} style={{ marginLeft: "auto", flex: "none" }} />
            </button>
          )}

          <div className="crail__end">
            <span>BroxiAI</span>
            <span className="grow" />
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              icon={theme === "dark" ? "sun" : "moon"}
              title={`Switch to ${theme === "dark" ? "light" : "dark"}`}
              onClick={toggleTheme}
            />
          </div>
        </div>
      </aside>

      <main className="cdoc">
        <article className="cdoc__doc">
          <header>
            <div className="overline ephead__over">
              <span className={`verb verb--${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
              {endpoint.flow && (
                <>
                  <i />
                  <span>{endpoint.flow}</span>
                </>
              )}
            </div>
            <h1 className="ephead__path">
              <PathText value={endpoint.path} />
            </h1>
            <p className="ephead__title">{endpoint.desc}</p>
          </header>

          <p className="deck epdeck">{endpoint.detail}</p>

          <dl className="deflist epmeta">
            <dt>Auth</dt>
            <dd>{endpoint.auth ? "API key required" : "Public — no key needed"}</dd>

            <dt>Cost</dt>
            <dd>
              {endpoint.credit ? (
                <span className="epmeta__cost">{endpoint.credit} −1</span>
              ) : (
                <span className="epmeta__free">No credit spent</span>
              )}
            </dd>

            {endpoint.slow && (
              <>
                <dt>Runtime</dt>
                <dd>Long-running — expect tens of seconds, not milliseconds</dd>
              </>
            )}

            <dt>Returns</dt>
            <dd>
              <code>{endpoint.response}</code>
            </dd>
          </dl>

          {endpoint.note && <aside className="marginnote epnote">{endpoint.note}</aside>}

          <section className="sect">
            <div className="sect__hd">
              <h2>Request</h2>
              <span className="sect__line" />
            </div>

            {endpoint.fields.length === 0 ? (
              <p className="fld__none">Takes no parameters.</p>
            ) : (
              endpoint.fields.map((f) => (
                <FieldRow
                  key={f.name}
                  field={f}
                  value={valueOf(endpoint, f)}
                  autoFilled={Boolean(
                    f.autoFill && lastVideoId && form[endpoint.key]?.[f.name] === undefined,
                  )}
                  onChange={(v) => setValue(endpoint.key, f.name, v)}
                />
              ))
            )}

            <div className="sendrow">
              <Button variant="primary" loading={sending} icon="send" onClick={send}>
                {sending ? "Sending…" : "Send request"}
              </Button>
              {missingKey ? (
                <span className="sendrow__warn">Needs an API key — set it in the left column.</span>
              ) : (
                <span className="sendrow__hint">
                  or press <span className="kbd">⌘</span> <span className="kbd">↵</span>
                </span>
              )}
            </div>

            {curl && (
              <details className="curl">
                <summary>
                  <Icon name="chevronRight" size={12} />
                  Equivalent cURL
                </summary>
                <div className="curl__body">
                  {curl}
                  <Button
                    className="curl__copy"
                    size="sm"
                    variant="ghost"
                    iconOnly
                    icon="copy"
                    title="Copy cURL"
                    onClick={async () =>
                      toast.show((await copyText(curl)) ? "cURL copied" : "Copy failed", "ok")
                    }
                  />
                </div>
              </details>
            )}
          </section>
        </article>

        <aside className="cdoc__res" ref={respRef}>
          <section className="sect">
            <div className="sect__hd">
              <h2>Response</h2>
              <span className="sect__line" />
              {response && (
                <>
                  <span className="respmeta">
                    <span className={`respstatus respstatus--${statusClass(response.status)}`}>
                      <span className="respstatus__dot" />
                      {response.status === 0 ? "NETWORK" : response.status}
                    </span>
                    <span>{response.ms} ms</span>
                    <span>{fmtBytes(response.bytes)}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    icon="copy"
                    title="Copy response"
                    onClick={async () =>
                      toast.show(
                        (await copyText(response.text)) ? "Copied to clipboard" : "Copy failed",
                        "ok",
                      )
                    }
                  />
                </>
              )}
            </div>

            <div className="respbox">
              {response ? (
                <pre className="respbody">
                  {response.isJson ? highlightJson(response.text) : response.text}
                </pre>
              ) : (
                <p className="respidle">Nothing sent yet.</p>
              )}
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Rail
 * ---------------------------------------------------------------------- */
function NavGroup({
  label,
  items,
  active,
  onPick,
  flow,
}: {
  label: string;
  items: Endpoint[];
  active: string;
  onPick: (key: string) => void;
  flow?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div className="crail__group">
      <div className="crail__label">{label}</div>
      <div className={`crail__items ${flow ? "crail__items--flow" : ""}`}>
        {items.map((e) => {
          const isActive = e.key === active;
          return (
            <button
              key={e.key}
              type="button"
              className={`eplink ${isActive ? "is-active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              onClick={() => onPick(e.key)}
            >
              <span className="eplink__gut">
                {flow ? (
                  <span className="eplink__step">{stepOf(e)}</span>
                ) : (
                  <span className={`verb verb--${e.method.toLowerCase()}`}>{e.method}</span>
                )}
              </span>
              <span className="eplink__path">{e.path}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Document pieces
 * ---------------------------------------------------------------------- */

/** `/scan/{scan_id}` — the slot is dimmed so the fixed part of the path leads. */
function PathText({ value }: { value: string }) {
  return (
    <>
      {value.split(/(\{[^}]+\})/g).map((part, i) =>
        part.startsWith("{") ? <em key={i}>{part}</em> : <span key={i}>{part}</span>,
      )}
    </>
  );
}

function FieldRow({
  field,
  value,
  autoFilled,
  onChange,
}: {
  field: Field;
  value: string | boolean;
  autoFilled: boolean;
  onChange: (v: string | boolean) => void;
}) {
  if (field.type === "checkbox") {
    return (
      <div className="fld">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className="fld__name">{field.label}</span>
        </label>
        {field.hint && <div className="fld__hint">{field.hint}</div>}
      </div>
    );
  }

  return (
    <div className="fld">
      <div className="fld__hd">
        <label className="fld__name" htmlFor={`f-${field.name}`}>
          {field.label}
        </label>
        {autoFilled && <span className="fld__auto">from /add</span>}
        <span className="fld__meta">
          {field.param}
          {field.optional ? " · optional" : ""}
        </span>
      </div>
      {field.type === "textarea" ? (
        <textarea
          id={`f-${field.name}`}
          className="textarea"
          rows={8}
          spellCheck={false}
          placeholder={field.placeholder}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : field.type === "select" ? (
        <select
          id={`f-${field.name}`}
          className="select"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={`f-${field.name}`}
          className="input mono"
          type="text"
          spellCheck={false}
          placeholder={field.placeholder}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {field.hint && <div className="fld__hint">{field.hint}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Request assembly
 *
 * One function builds the request, and both the sender and the cURL preview
 * read from it — so the snippet on screen can never describe a different
 * request from the one the button sends. `strict` is the only difference:
 * sending validates, previewing shows the half-filled form as it stands.
 * ---------------------------------------------------------------------- */
interface Assembled {
  url: string;
  body: Record<string, unknown>;
}

function assemble(
  ep: Endpoint,
  read: (f: Field) => FieldValue,
  strict: boolean,
): Assembled | { error: string } {
  let path: string = ep.path;
  const query = new URLSearchParams();
  const body: Record<string, unknown> = {};

  for (const f of ep.fields) {
    const raw = read(f);

    let value: unknown;
    if (f.type === "checkbox") {
      value = Boolean(raw);
    } else if (f.name === "report_list") {
      try {
        value = JSON.parse(String(raw || "{}"));
      } catch {
        if (strict) return { error: "report_list is not valid JSON" };
        value = {};
      }
    } else {
      value = String(raw).trim();
    }

    if (f.param === "path") {
      if (value === "" || value == null) {
        if (strict) return { error: `${f.label} is required` };
        continue; // leave `{name}` in place, so the preview still reads as a path
      }
      path = path.replace(`{${f.name}}`, encodeURIComponent(String(value)));
    } else if (f.param === "query") {
      // Skip blank optional filters — sending `?status=` is a request to
      // filter on the empty string, not a request for everything.
      if (f.optional && (value === "" || value == null)) continue;
      query.set(f.name, String(value));
    } else {
      if (f.optional && (value === "" || value == null)) continue;
      body[f.name] = value;
    }
  }

  return { url: query.toString() ? `${path}?${query}` : path, body };
}

/**
 * The key is only written into the snippet while it is visible on screen.
 * Copying a ready-to-run command is the point of the block, but a console is
 * often the thing on the shared screen — so hiding the key hides it here too.
 */
function buildCurl(ep: Endpoint, req: Assembled, apiKey: string, revealKey: boolean): string {
  const url = `${location.origin}${req.url}`;
  const lines = [ep.method === "GET" ? `curl '${url}'` : `curl -X POST '${url}'`];
  if (ep.auth) lines.push(`  -H 'key: ${revealKey && apiKey ? apiKey : "$DIPNET_API_KEY"}'`);
  if (ep.method === "POST") {
    lines.push(`  -H 'Content-Type: application/json'`);
    lines.push(`  -d '${JSON.stringify(req.body)}'`);
  }
  return lines.join(" \\\n");
}

const statusClass = (status: number): "2xx" | "4xx" | "5xx" | "err" => {
  if (status === 0) return "err";
  if (status >= 200 && status < 300) return "2xx";
  if (status >= 400 && status < 500) return "4xx";
  return "5xx";
};

/** Re-indent a JSON body; leave anything unparseable exactly as it arrived. */
function safePretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
